/**
 * T-085 — Session state for the tnd-support SPA (spec §8 src/state.ts).
 *
 * Holds the three pieces of session-scoped state the views need:
 *
 *  1. **Operator token** — session-only. Persisted to `sessionStorage` (never
 *     `localStorage`, never logged, never embedded in markup) so a page
 *     reload keeps the session but closing the tab drops it (spec §3).
 *  2. **Selection** — the release/artifact an operator is working with, plus
 *     the command-generator inputs (Python ABI, serving target, expiry).
 *     Selection is kept coherent: changing the version clears any artifact
 *     from a different version; selecting an artifact pins its version.
 *  3. **Issued links** — links created this session. `CreateLinkResponse.url`
 *     is returned by the server exactly once, so the session retains it for
 *     display/copy; the list is bounded (default 100, newest first).
 *
 * The store is framework-agnostic (`getSnapshot`/`subscribe`, compatible
 * with `useSyncExternalStore`); React hooks at the bottom bind the default
 * singleton. `sessionApi` is a pre-wired {@link ApiClient} whose token comes
 * from the store and which drops the token on any server 401.
 */

import { useSyncExternalStore } from 'react';
import type {
  Artifact,
  CreateLinkResponse,
  LinkExpiry,
  LinkSummary,
  PythonAbi,
  ServingTarget,
} from '../shared/api';
import { createApiClient, type ApiClient } from './api';

/** sessionStorage key for the operator token (session-only, spec §3). */
export const SESSION_TOKEN_STORAGE_KEY = 'tnd-support:operator-token:v1';

/** A link issued during this session, including its one-time download URL. */
export interface IssuedLink {
  /** Server-reported metadata; replaced via {@link SessionStore.applyLinkUpdate} after revoke. */
  link: LinkSummary;
  /** One-time URL from issuance — never listable from the server afterwards. */
  url: string;
  /** False for `gcs_v4` direct URLs (non-revocable bearer capabilities, spec §4). */
  revocable: boolean;
  /** ISO-8601 client-side issuance timestamp. */
  issuedAt: string;
}

/** Immutable session state snapshot; referentially stable between mutations. */
export interface SessionSnapshot {
  /** Operator bearer token; null when signed out. */
  readonly token: string | null;
  /** Selected release version (normalized semver, no leading `v`). */
  readonly version: string | null;
  /** Selected artifact; always belongs to `version` when both are set. */
  readonly artifact: Artifact | null;
  /** Command-generator input: Python ABI (spec §5 Page 2). */
  readonly pythonAbi: PythonAbi;
  /** Command-generator input: serving target (spec §5 Page 2). */
  readonly servingTarget: ServingTarget;
  /** Command-generator / link-issuance input: server-bounded expiry (spec §3). */
  readonly expiry: LinkExpiry;
  /** Links issued this session, newest first. */
  readonly issuedLinks: readonly IssuedLink[];
}

export interface SessionStore {
  /** Current immutable snapshot (stable reference until the next mutation). */
  getSnapshot(): SessionSnapshot;
  /** Subscribe to mutations; returns an unsubscribe function. */
  subscribe(listener: () => void): () => void;
  /**
   * Sign in: store a non-empty operator token in memory and sessionStorage.
   * Throws on blank input so views surface the rejection instead of
   * silently staying signed out.
   */
  setToken(token: string): void;
  /**
   * Drop the token (memory + sessionStorage) but keep selection and issued
   * links — used when the server rejects the token with 401 so the operator
   * can re-authenticate without losing context.
   */
  clearToken(): void;
  /** Full sign-out: token, selection, and issued links all reset. */
  resetSession(): void;
  /** Select a release version; clears the artifact if it belongs elsewhere. */
  selectVersion(version: string | null): void;
  /** Select an artifact; pins `version` to the artifact's own version. */
  selectArtifact(artifact: Artifact | null): void;
  setPythonAbi(pythonAbi: PythonAbi): void;
  setServingTarget(servingTarget: ServingTarget): void;
  setExpiry(expiry: LinkExpiry): void;
  /** Record a freshly issued link (keeps the one-time URL). */
  recordIssuedLink(response: CreateLinkResponse): void;
  /** Replace the metadata of a session link after a server-side change (revoke). */
  applyLinkUpdate(link: LinkSummary): void;
}

export interface CreateSessionStoreOptions {
  /** Injectable storage for tests; defaults to window.sessionStorage when available. */
  storage?: Storage | null;
  /** Storage key override; defaults to {@link SESSION_TOKEN_STORAGE_KEY}. */
  storageKey?: string;
  /** Maximum retained issued links; oldest are dropped. Defaults to 100. */
  maxIssuedLinks?: number;
}

function sessionStorageOrNull(): Storage | null {
  try {
    // Access itself can throw in hardened privacy modes.
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** Create an independent session store (the app uses the {@link sessionStore} singleton). */
export function createSessionStore(options: CreateSessionStoreOptions = {}): SessionStore {
  const storage = options.storage !== undefined ? options.storage : sessionStorageOrNull();
  const storageKey = options.storageKey ?? SESSION_TOKEN_STORAGE_KEY;
  const maxIssuedLinks = options.maxIssuedLinks ?? 100;

  let initialToken: string | null = null;
  try {
    initialToken = storage?.getItem(storageKey) ?? null;
  } catch {
    // Storage unreadable (quota/privacy mode) — operate memory-only.
  }

  let snapshot: SessionSnapshot = {
    token: initialToken,
    version: null,
    artifact: null,
    pythonAbi: 'py3.10',
    servingTarget: 'all',
    expiry: '24h',
    issuedLinks: [],
  };

  const listeners = new Set<() => void>();

  function update(patch: Partial<SessionSnapshot>): void {
    snapshot = { ...snapshot, ...patch };
    for (const listener of listeners) listener();
  }

  function persistToken(token: string | null): void {
    try {
      if (token === null) storage?.removeItem(storageKey);
      else storage?.setItem(storageKey, token);
    } catch {
      // Persistence is best-effort; the in-memory session still works.
    }
  }

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setToken: (token) => {
      const trimmed = token.trim();
      if (trimmed === '') throw new Error('Operator token must be a non-empty string');
      persistToken(trimmed);
      update({ token: trimmed });
    },
    clearToken: () => {
      persistToken(null);
      update({ token: null });
    },
    resetSession: () => {
      persistToken(null);
      update({ token: null, version: null, artifact: null, issuedLinks: [] });
    },
    selectVersion: (version) => {
      update({
        version,
        artifact: snapshot.artifact !== null && snapshot.artifact.version === version ? snapshot.artifact : null,
      });
    },
    selectArtifact: (artifact) => {
      update({ artifact, version: artifact === null ? snapshot.version : artifact.version });
    },
    setPythonAbi: (pythonAbi) => update({ pythonAbi }),
    setServingTarget: (servingTarget) => update({ servingTarget }),
    setExpiry: (expiry) => update({ expiry }),
    recordIssuedLink: (response) => {
      const entry: IssuedLink = {
        link: response.link,
        url: response.url,
        revocable: response.revocable,
        issuedAt: new Date().toISOString(),
      };
      update({ issuedLinks: [entry, ...snapshot.issuedLinks].slice(0, maxIssuedLinks) });
    },
    applyLinkUpdate: (link) => {
      if (!snapshot.issuedLinks.some((entry) => entry.link.id === link.id)) return;
      update({
        issuedLinks: snapshot.issuedLinks.map((entry) =>
          entry.link.id === link.id ? { ...entry, link } : entry,
        ),
      });
    },
  };
}

/** Application-wide session store singleton. */
export const sessionStore: SessionStore = createSessionStore();

/**
 * Pre-wired API client bound to {@link sessionStore}: reads the token at
 * call time and drops it from the session whenever the server answers 401.
 */
export const sessionApi: ApiClient = createApiClient({
  getToken: () => sessionStore.getSnapshot().token,
  onUnauthorized: () => sessionStore.clearToken(),
});

/** Full session snapshot; re-renders on any session mutation. */
export function useSessionSnapshot(store: SessionStore = sessionStore): SessionSnapshot {
  return useSyncExternalStore(store.subscribe, store.getSnapshot);
}

/** Operator token (null when signed out). */
export function useSessionToken(store: SessionStore = sessionStore): string | null {
  return useSyncExternalStore(store.subscribe, store.getSnapshot).token;
}

/** Links issued this session (newest first), including their one-time URLs. */
export function useIssuedLinks(store: SessionStore = sessionStore): readonly IssuedLink[] {
  return useSyncExternalStore(store.subscribe, store.getSnapshot).issuedLinks;
}
